import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });
api.defaults.withCredentials = true;

// Active workspace is sent on every request via the X-Workspace-Id header.
let _activeWs = null;
try {
  _activeWs = localStorage.getItem("iqm_active_ws") || null;
} catch {
  _activeWs = null;
}
if (_activeWs) api.defaults.headers.common["X-Workspace-Id"] = _activeWs;

export const setActiveWorkspaceHeader = (id) => {
  _activeWs = id || null;
  if (id) {
    api.defaults.headers.common["X-Workspace-Id"] = id;
    try {
      localStorage.setItem("iqm_active_ws", id);
    } catch (e) {
      console.warn("Could not persist active workspace:", e);
    }
  } else {
    delete api.defaults.headers.common["X-Workspace-Id"];
    try {
      localStorage.removeItem("iqm_active_ws");
    } catch (e) {
      console.warn("Could not clear active workspace:", e);
    }
  }
};

export const fileUrl = (storage_path) =>
  `${API}/files/${storage_path}`;

export const formatINR = (n) => {
  const num = Number(n || 0);
  return "₹" + num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

// Returns dimensions in inches for a measurement, gracefully falling back to
// legacy feet values stored on older quotations.
export const dimInches = (m) => {
  const lIn = parseFloat(m?.length_in);
  const hIn = parseFloat(m?.height_in);
  const l = Number.isFinite(lIn)
    ? lIn
    : (parseFloat(m?.length_ft) || 0) * 12;
  const h = Number.isFinite(hIn)
    ? hIn
    : (parseFloat(m?.height_ft) || 0) * 12;
  return { l, h };
};

export const sqft = (m) => {
  const { l, h } = dimInches(m);
  return (l * h) / 144;
};

// A measurement/accessory/room counts toward totals unless explicitly excluded.
export const isIncluded = (x) => x?.is_included !== false;

// Returns { total, modular, non_modular } in sqft for a room (excluded items skipped).
export const roomSqftBreakdown = (room) => {
  let modular = 0;
  let non_modular = 0;
  if (isIncluded(room)) {
    for (const m of room.measurements || []) {
      if (!isIncluded(m)) continue;
      const a = sqft(m);
      if (m.is_modular) modular += a;
      else non_modular += a;
    }
  }
  return { total: modular + non_modular, modular, non_modular };
};

// Returns { total, modular, non_modular } across ALL rooms.
export const roomsSqftBreakdown = (rooms) => {
  let modular = 0;
  let non_modular = 0;
  (rooms || []).forEach((r) => {
    const b = roomSqftBreakdown(r);
    modular += b.modular;
    non_modular += b.non_modular;
  });
  return { total: modular + non_modular, modular, non_modular };
};

// Human-friendly one-liner. If only one bucket is non-zero, drop the split.
// Examples:
//   "320 sqft (Modular 200 · Frame & Shutter 120)"
//   "320 sqft (Modular 320)"
//   "320 sqft"
export const formatSqftBreakdown = ({ total, modular, non_modular }) => {
  const parts = splitSqftBreakdown({ total, modular, non_modular });
  return parts.detail ? `${parts.main} ${parts.detail}` : parts.main;
};

// Returns { main, detail } so callers can style the detail portion differently.
// detail is either "(Modular X · Frame & Shutter Y)" or "(Modular X)" or
// "(Frame & Shutter Y)" — always present whenever any non-zero buckets exist.
export const splitSqftBreakdown = ({ total, modular, non_modular }) => {
  const t = Number(total || 0);
  const m = Number(modular || 0);
  const n = Number(non_modular || 0);
  const pieces = [];
  if (m > 0) pieces.push(`Modular ${m.toFixed(1)}`);
  if (n > 0) pieces.push(`Frame & Shutter ${n.toFixed(1)}`);
  return {
    main: `${t.toFixed(1)} sqft`,
    detail: pieces.length > 0 ? `(${pieces.join(" · ")})` : "",
  };
};

export const formatDims = (m) => {
  const { l, h } = dimInches(m);
  return `${l || 0}" × ${h || 0}"`;
};

export const calcAreaCost = (rooms, modularMultiplier = 2.5) => {
  let total = 0;
  const mm = Math.max(0, parseFloat(modularMultiplier) || 0);
  for (const room of rooms || []) {
    if (!isIncluded(room)) continue;
    for (const m of room.measurements || []) {
      if (!isIncluded(m)) continue;
      const factor = m.is_modular ? mm : 1;
      total += sqft(m) * (parseFloat(m.finish_price) || 0) * factor;
    }
  }
  return total;
};

export const calcAccessoriesCost = (accessories) => {
  return (accessories || []).reduce(
    (s, a) =>
      isIncluded(a)
        ? s + (parseFloat(a.quantity) || 0) * (parseFloat(a.price_per_unit) || 0)
        : s,
    0
  );
};

export const computeBreakdown = (data, modularMultiplier = 2.5) => {
  const area = calcAreaCost(data.rooms, modularMultiplier);
  const acc = calcAccessoriesCost(data.accessories);
  const subtotal = area + acc;
  const discount = Math.max(0, parseFloat(data.discount_amount) || 0);
  const taxPct = Math.max(0, parseFloat(data.tax_percent) || 0);
  const afterDisc = Math.max(0, subtotal - discount);
  const tax = (afterDisc * taxPct) / 100;
  const total = afterDisc + tax;
  return { area, acc, subtotal, discount, taxPct, tax, total };
};

export const publicQuotationUrl = (id) =>
  `${window.location.origin}/q/${id}`;

// ----- Quotation status (Draft / Sent / Approved) -----
export const QUOTE_STATUSES = ["draft", "sent", "approved"];
export const QUOTE_STATUS_META = {
  draft: { label: "Draft", color: "#A6ADBB", bg: "rgba(166,173,187,0.12)", border: "rgba(166,173,187,0.35)" },
  sent: { label: "Sent", color: "#9AB8D8", bg: "rgba(154,184,216,0.12)", border: "rgba(154,184,216,0.40)" },
  approved: { label: "Approved", color: "#8FB290", bg: "rgba(143,178,144,0.14)", border: "rgba(143,178,144,0.45)" },
};
export const normalizeStatus = (s) =>
  QUOTE_STATUSES.includes(s) ? s : "draft";
export const updateQuotationStatus = (id, status) =>
  api.patch(`/quotations/${id}/status`, { status });

// ----- Role permissions matrix (admin control panel) -----
export const getPermissions = () => api.get("/settings/permissions");
export const savePermissions = (matrix) => api.put("/settings/permissions", { matrix });

// Human-friendly relative time, e.g. "just now", "5m ago", "3h ago", "2d ago".
export const timeAgo = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

// ----- Tier pricing helpers -----
const tierFactor = (pct) => Math.max(0, 1 - (parseFloat(pct) || 0) / 100);

const breakdownForTier = (data, finishPct, hardwarePct, modularMultiplier = 2.5) => {
  const fFactor = tierFactor(finishPct);
  const hFactor = tierFactor(hardwarePct);
  const mm = Math.max(0, parseFloat(modularMultiplier) || 0);
  const rooms = [];
  let area = 0;
  (data.rooms || []).forEach((room) => {
    let rTotal = 0;
    if (isIncluded(room)) {
      (room.measurements || []).forEach((m) => {
        if (!isIncluded(m)) return;
        const modFactor = m.is_modular ? mm : 1;
        rTotal += sqft(m) * (parseFloat(m.finish_price) || 0) * modFactor * fFactor;
      });
    }
    rooms.push({ id: room.id, area_cost: rTotal });
    area += rTotal;
  });
  const acc = (data.accessories || []).reduce(
    (s, a) => (isIncluded(a) ? s + (parseFloat(a.quantity) || 0) * (parseFloat(a.price_per_unit) || 0) * hFactor : s),
    0
  );
  const subtotal = area + acc;
  const discount = Math.max(0, parseFloat(data.discount_amount) || 0);
  const taxPct = Math.max(0, parseFloat(data.tax_percent) || 0);
  const afterDisc = Math.max(0, subtotal - discount);
  const tax = (afterDisc * taxPct) / 100;
  const total = afterDisc + tax;
  return {
    rooms,
    area_cost: area,
    accessories_cost: acc,
    subtotal,
    discount,
    tax_amount: tax,
    tax_percent: taxPct,
    grand_total: total,
    discount_percent: parseFloat(finishPct) || 0,
    hardware_discount_percent: parseFloat(hardwarePct) || 0,
  };
};

export const computeTieredBreakdown = (data, tierSettings) => {
  const eco = tierSettings?.economy_discount ?? 35;
  const pre = tierSettings?.premium_discount ?? 15;
  // Hardware discount: fall back to finish discount if not provided (legacy)
  const ecoHw = tierSettings?.economy_hardware_discount ?? eco;
  const preHw = tierSettings?.premium_hardware_discount ?? pre;
  const mm = tierSettings?.modular_multiplier ?? 2.5;
  return {
    economy_pct: eco,
    premium_pct: pre,
    economy_hardware_pct: ecoHw,
    premium_hardware_pct: preHw,
    modular_multiplier: mm,
    breakdown: {
      luxury: breakdownForTier(data, 0, 0, mm),
      premium: breakdownForTier(data, pre, preHw, mm),
      economy: breakdownForTier(data, eco, ecoHw, mm),
    },
  };
};

// Ordered list (for UI rendering: Luxury → Premium → Economy)
export const TIER_ORDER = [
  { key: "luxury", label: "Luxury", hint: "Master prices" },
  { key: "premium", label: "Premium", hint: "Mid-range finish" },
  { key: "economy", label: "Economy", hint: "Budget-friendly" },
];

// ----- V2 dynamic tiers -----
const _tierFactor = (pct) => Math.max(0, 1 - (parseFloat(pct) || 0) / 100);

// Resolve the per-sqft finish rate for a measurement at a given tier, applying
// the cascade rule: when blank, walk back up the ordered tier list and inherit
// the price of the first tier above (Economy blank → Premium price; both blank
// → base price). Mirrors the backend's _resolve_finish_rate.
export const resolveFinishRateV2 = (m, tier, orderedTiers, finishPct) => {
  const base = parseFloat(m?.finish_price) || 0;
  const tp = m?.finish_tier_prices || {};
  if (!tier?.id || !Array.isArray(orderedTiers) || orderedTiers.length === 0) {
    return base * _tierFactor(finishPct);
  }
  const pos = orderedTiers.findIndex((t) => t.id === tier.id);
  if (pos < 0) return base;
  if (orderedTiers[pos].is_base) return base;
  for (let i = pos; i >= 0; i--) {
    const cand = orderedTiers[i];
    if (cand.is_base) return base;
    const v = tp[cand.id];
    if (v != null) {
      const num = parseFloat(v);
      if (Number.isFinite(num)) return num;
    }
  }
  return base;
};

// Compute a v2 breakdown for a single tier definition.
const breakdownForTierV2 = (data, tier, globalMm, orderedTiers) => {
  const hFactor = _tierFactor(tier.hardware_discount);
  const mm = Math.max(0, parseFloat(
    tier.modular_multiplier == null ? globalMm : tier.modular_multiplier,
  ) || 0);
  const rooms = [];
  let area = 0;
  (data.rooms || []).forEach((room) => {
    let rTotal = 0;
    if (isIncluded(room)) {
      (room.measurements || []).forEach((m) => {
        if (!isIncluded(m)) return;
        const modFactor = m.is_modular ? mm : 1;
        const pricePerSqft = resolveFinishRateV2(
          m,
          tier,
          orderedTiers,
          tier.finish_discount,
        );
        rTotal += sqft(m) * pricePerSqft * modFactor;
      });
    }
    rooms.push({ id: room.id, area_cost: rTotal });
    area += rTotal;
  });
  const acc = (data.accessories || []).reduce(
    (s, a) =>
      isIncluded(a)
        ? s + (parseFloat(a.quantity) || 0) * (parseFloat(a.price_per_unit) || 0) * hFactor
        : s,
    0
  );
  const subtotal = area + acc;
  const discount = Math.max(0, parseFloat(data.discount_amount) || 0);
  const taxPct = Math.max(0, parseFloat(data.tax_percent) || 0);
  const afterDisc = Math.max(0, subtotal - discount);
  const tax = (afterDisc * taxPct) / 100;
  const total = afterDisc + tax;
  return {
    rooms,
    area_cost: area,
    accessories_cost: acc,
    subtotal,
    discount_amount: discount,
    tax_amount: tax,
    tax_percent: taxPct,
    grand_total: total,
    discount_percent: parseFloat(tier.finish_discount) || 0,
    hardware_discount_percent: parseFloat(tier.hardware_discount) || 0,
    modular_multiplier: mm,
  };
};

// Build a v2 tiers snapshot from a TierConfigV2 ({tiers, modular_multiplier}) and a set of selected ids.
export const computeTieredBreakdownV2 = (data, tierConfigV2, selectedIds) => {
  const all = (tierConfigV2?.tiers || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const globalMm = parseFloat(tierConfigV2?.modular_multiplier ?? 2.5) || 2.5;
  const sel = Array.isArray(selectedIds) && selectedIds.length > 0
    ? new Set(selectedIds)
    : null;
  const chosen = sel ? all.filter((t) => sel.has(t.id)) : all;
  const list = (chosen.length ? chosen : all).map((t) => ({
    id: t.id,
    name: t.name,
    description: t.description || "",
    color: t.color || "#C5A880",
    finish_discount: parseFloat(t.finish_discount) || 0,
    hardware_discount: parseFloat(t.hardware_discount) || 0,
    modular_multiplier: t.modular_multiplier == null ? globalMm : parseFloat(t.modular_multiplier),
    is_base: Boolean(t.is_base),
    breakdown: breakdownForTierV2(data, t, globalMm, all),
  }));
  return {
    version: 2,
    modular_multiplier: globalMm,
    selected_tier_ids: list.map((t) => t.id),
    list,
  };
};

// True if this tiers snapshot uses the v2 shape ({list: [...], version: 2}).
export const isTiersV2 = (tiers) =>
  Boolean(tiers && (tiers.version === 2 || Array.isArray(tiers.list)));

// Convert a v1 legacy tiers snapshot to a v2 "list" form so downstream renderers can use a single code path.
export const adaptLegacyTiersToV2 = (tiers) => {
  if (!tiers || !tiers.breakdown) return { version: 2, modular_multiplier: 2.5, selected_tier_ids: [], list: [] };
  const map = {
    luxury: { name: "Luxury", color: "#C5A880" },
    premium: { name: "Premium", color: "#9AB8D8" },
    economy: { name: "Economy", color: "#8FB290" },
  };
  const order = ["luxury", "premium", "economy"];
  const list = order
    .filter((k) => tiers.breakdown[k])
    .map((k) => {
      const bt = tiers.breakdown[k];
      return {
        id: k,
        name: map[k].name,
        description: "",
        color: map[k].color,
        finish_discount: bt.discount_percent || 0,
        hardware_discount: bt.hardware_discount_percent || 0,
        modular_multiplier: tiers.modular_multiplier || 2.5,
        is_base: k === "luxury",
        breakdown: bt,
      };
    });
  return {
    version: 2,
    modular_multiplier: tiers.modular_multiplier || 2.5,
    selected_tier_ids: list.map((t) => t.id),
    list,
  };
};

// Return the v2 list regardless of input shape (snapshot OR live config+selection).
export const resolveTiersV2 = (data, tiersSnapshot, tierConfigV2, selectedIds) => {
  if (tiersSnapshot && isTiersV2(tiersSnapshot) && Array.isArray(tiersSnapshot.list) && tiersSnapshot.list.length) {
    return tiersSnapshot;
  }
  if (tiersSnapshot && tiersSnapshot.breakdown) {
    return adaptLegacyTiersToV2(tiersSnapshot);
  }
  if (tierConfigV2 && Array.isArray(tierConfigV2.tiers) && tierConfigV2.tiers.length) {
    return computeTieredBreakdownV2(data, tierConfigV2, selectedIds);
  }
  return { version: 2, modular_multiplier: 2.5, selected_tier_ids: [], list: [] };
};
